import { useCallback, useEffect, useRef } from 'react';
import {
  useAnalyticsCategory,
  useAnalyticsSessionId,
} from './AnalyticsCategoryProvider';
import { useAnalyticsContext } from './AnalyticsProvider';
import { Categories } from './categories';

const ACCOUNT_ID_KEY = 'ACCOUNT_ID_KEY';

export const useTrack = (overrideCat?: Categories) => {
  const { analytics } = useAnalyticsContext();
  const category = useAnalyticsCategory();
  const sessionId = useAnalyticsSessionId();

  return useCallback(
    async (event: string, traits?: {}) => {
      const group = analytics.group();
      analytics.event({
        category: overrideCat || category,
        action: event,
        organizationId: group,
        sessionId,
        ...traits,
      });
    },
    [analytics, category],
  );
};

export const useGroup = () => {
  const { analytics } = useAnalyticsContext();
  const prev = useRef<any>();
  return useCallback(
    (organizationId: string, data?: {}) => {
      const group = analytics.group();
      if (group === organizationId) {
        return;
      }
      analytics.group(organizationId, data);
    },
    [analytics, prev],
  );
};

export const useUpdateIdentify = () => {
  const { analytics } = useAnalyticsContext();
  return useCallback(
    (id: string, info: any) => {
      const prevUpdateIdentifyId = localStorage.getItem(ACCOUNT_ID_KEY);
      if (prevUpdateIdentifyId === id && prevUpdateIdentifyId === info?.id) {
        return;
      }
      localStorage.setItem(ACCOUNT_ID_KEY, id);
      analytics.identify(info, id);
    },
    [analytics],
  );
};

export const useIdentifyAccount = () => {
  const identify = useUpdateIdentify();
  return useCallback(
    (account: any) => identify(account.id, account),
    [identify],
  );
};

export const useIdentifyProspect = () => {
  const { analytics } = useAnalyticsContext();
  return useCallback((info: any) => analytics.identify(info), [analytics]);
};

export const useIdentifyAnonymous = () => {
  const { analytics } = useAnalyticsContext();
  return useCallback(() => {
    analytics.identifyAnonymous();
  }, [analytics]);
};

export const useTrackLogout = () => {
  const { analytics } = useAnalyticsContext();
  return useCallback(() => analytics.logout(), [analytics]);
};

export const useTrackEvent = <D extends {}>(event: string, traits?: D) => {
  const track = useTrack();

  return useCallback(
    (params?: D) => track(event, { ...traits, ...params }),
    [track, event, traits],
  );
};

export const useInstantTrack = (event: string, traits?: {}) => {
  const track = useTrackEvent(event, traits);
  return useEffect(() => {
    track();
  }, [track]);
};
